import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaClipboardList,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaSpinner,
  FaInfoCircle,
  FaPlay,
} from "react-icons/fa";
import { useAuth } from "../../../context/AuthContext";
import PageHeader from "../../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type AssignedExam = {
  assignmentId: number;
  examSetId: number;
  examSetName: string;
  level: string;
  scheduledStartTime: string;
  scheduledEndTime: string;
  scheduledStartTimeBDT: string;
  scheduledEndTimeBDT: string;
  status: string;
  totalDurationMinutes: number;
  canStart: boolean;
  hasAttempt: boolean;
  attemptId: number | null;
  attemptStatus: string | null;
  canContinue: boolean;
  resultsPublished: boolean;
  totalScore: number | null;
  maxScore: number | null;
  percentage: number | null;
  passed: boolean | null;
};

const StudentMyExams: React.FC = () => {
  const { student } = useAuth();
  const navigate = useNavigate();

  const [exams, setExams] = useState<AssignedExam[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [startingExamId, setStartingExamId] = useState<number | null>(null);

  useEffect(() => {
    fetchExams();
  }, []);

  const fetchExams = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/student/exams/my-exams`,
        {
          credentials: "include",
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log("API Response:", result);

        const examData = result.data || result;
        const examsArray = Array.isArray(examData) ? examData : [];

        console.log("Parsed exams:", examsArray);
        setExams(examsArray);
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Failed to load exams");
      }
    } catch (err) {
      console.error("Network error:", err);
      setError("Network error - please check your connection");
    } finally {
      setLoading(false);
    }
  };

  // ✅ FIXED: Now uses backend status instead of client-side calculation
  const getExamStatus = (exam: AssignedExam) => {
    // If has attempt and it's completed
    if (exam.hasAttempt && exam.attemptStatus) {
      const status = exam.attemptStatus.toUpperCase();

      if (
        status === "SUBMITTED" ||
        status === "AUTO_SUBMITTED" ||
        status === "GRADED" ||
        status === "PARTIALLY_GRADED"
      ) {
        return "completed";
      }

      if (status === "IN_PROGRESS" && exam.canContinue) {
        return "inprogress";
      }
    }

    // ✅ FIX: Use backend status instead of calculating ourselves
    // Backend now returns: SCHEDULED, ACTIVE, COMPLETED, CANCELLED
    if (exam.status) {
      const backendStatus = exam.status.toUpperCase();

      switch (backendStatus) {
        case "SCHEDULED":
          return "upcoming";
        case "ACTIVE":
          return "available";
        case "COMPLETED":
          return "expired"; // ✅ Changed from "missed" to "expired"
        case "CANCELLED":
          return "cancelled";
        default:
          return "unavailable";
      }
    }

    // Fallback if no status from backend
    return "unavailable";
  };

  // ✅ FIXED: Replaced "missed" with "expired" and added "cancelled"
  const getStatusBadge = (status: string) => {
    const configs = {
      completed: {
        label: "Completed",
        icon: <FaCheckCircle />,
        className: "bg-emerald-100 text-emerald-800 border-emerald-200",
      },
      inprogress: {
        label: "In Progress",
        icon: <FaClock />,
        className: "bg-blue-100 text-blue-800 border-blue-200",
      },
      available: {
        label: "Available",
        icon: <FaClock />,
        className: "bg-green-100 text-green-800 border-green-200",
      },
      upcoming: {
        label: "Upcoming",
        icon: <FaClock />,
        className: "bg-yellow-100 text-yellow-800 border-yellow-200",
      },
      expired: {
        // ✅ Changed from "missed"
        label: "Expired",
        icon: <FaTimesCircle />,
        className: "bg-gray-100 text-gray-800 border-gray-200",
      },
      cancelled: {
        // ✅ Added cancelled status
        label: "Cancelled",
        icon: <FaTimesCircle />,
        className: "bg-red-100 text-red-800 border-red-200",
      },
      unavailable: {
        label: "Unavailable",
        icon: <FaTimesCircle />,
        className: "bg-gray-100 text-gray-800 border-gray-200",
      },
    };

    const config =
      configs[status as keyof typeof configs] || configs.unavailable;

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${config.className}`}
      >
        {config.icon}
        {config.label}
      </span>
    );
  };

  const handleStartExam = (exam: AssignedExam) => {
    console.log("Starting exam with assignmentId:", exam.assignmentId);

    if (!exam.assignmentId) {
      alert("Error: Invalid assignment ID");
      return;
    }

    if (startingExamId !== null) {
      console.log("Already starting an exam, ignoring duplicate click");
      return;
    }

    setStartingExamId(exam.assignmentId);
    navigate(`/student/exam/${exam.assignmentId}/take`);
  };

  const handleContinueExam = (exam: AssignedExam) => {
    console.log("Continuing exam with assignmentId:", exam.assignmentId);

    if (!exam.assignmentId) {
      alert("Error: Invalid assignment ID");
      return;
    }

    if (startingExamId !== null) {
      console.log("Already starting an exam, ignoring duplicate click");
      return;
    }

    setStartingExamId(exam.assignmentId);
    navigate(`/student/exam/${exam.assignmentId}/take`);
  };

  const handleViewResult = (exam: AssignedExam) => {
    console.log("Viewing result with attemptId:", exam.attemptId);

    if (!exam.attemptId) {
      alert("Error: Invalid attempt ID");
      return;
    }

    navigate(`/student/exam/${exam.attemptId}/result`);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaClipboardList />}
        title="My Exams"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/student/student-dashboard" },
          { label: "My Exams" },
        ]}
      />

      {/* Info Card */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
            <FaInfoCircle className="text-blue-600" />
            How It Works
          </h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">1.</span>
              <span>View your assigned exams with deadlines below</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">2.</span>
              <span>
                Click "Start Exam" when ready (exam has 3 modules: Lesen, Hören,
                Schreiben)
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">3.</span>
              <span>Answer all questions before time expires</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 font-bold">4.</span>
              <span>
                Submit exam and wait for your mentor to grade and publish
                results
              </span>
            </li>
          </ul>
        </div>

        {/* Exams List */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white p-6">
            <h3 className="text-xl font-bold flex items-center gap-2">
              <FaClipboardList />
              Assigned Exams
            </h3>
            <p className="text-cyan-100 text-sm mt-1">
              Complete exams and track your progress
            </p>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <FaSpinner className="animate-spin text-[#009fe3] text-4xl mx-auto mb-4" />
                <p className="text-gray-600">Loading your exams...</p>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            ) : exams.length === 0 ? (
              <div className="text-center py-12">
                <FaClipboardList className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                <h4 className="text-lg font-medium text-gray-700 mb-2">
                  No Exams Assigned
                </h4>
                <p className="text-gray-500 text-sm">
                  No exams have been assigned to you yet.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                        Exam Name
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                        Level
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                        Duration
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                        Start Time
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                        End Time
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                        Status
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                        Score
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {exams.map((exam, index) => {
                      const status = getExamStatus(exam);
                      return (
                        <tr
                          key={exam.assignmentId}
                          className={`border-b border-gray-100 ${
                            index % 2 === 1 ? "bg-gray-50" : "bg-white"
                          } hover:bg-blue-50 transition-colors`}
                        >
                          <td className="py-4 px-4">
                            <span className="font-semibold text-gray-900">
                              {exam.examSetName}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                              {exam.level}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-gray-600 text-sm">
                            {exam.totalDurationMinutes} min
                          </td>
                          <td className="py-4 px-4 text-gray-600 text-sm">
                            {exam.scheduledStartTimeBDT ||
                              new Date(exam.scheduledStartTime).toLocaleString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }
                              )}
                          </td>
                          <td className="py-4 px-4 text-gray-600 text-sm">
                            {exam.scheduledEndTimeBDT ||
                              new Date(exam.scheduledEndTime).toLocaleString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }
                              )}
                          </td>
                          <td className="py-4 px-4">
                            {getStatusBadge(status)}
                          </td>
                          <td className="py-4 px-4">
                            {exam.resultsPublished &&
                            exam.totalScore !== null ? (
                              <div className="text-sm">
                                <span className="font-semibold text-gray-900">
                                  {exam.totalScore}/{exam.maxScore}
                                </span>
                                <div className="text-xs text-gray-500">
                                  {exam.percentage?.toFixed(1)}%
                                  {exam.passed && (
                                    <span className="ml-1 text-green-600 font-semibold">
                                      ✓ Passed
                                    </span>
                                  )}
                                  {exam.passed === false && (
                                    <span className="ml-1 text-red-600 font-semibold">
                                      ✗ Failed
                                    </span>
                                  )}
                                </div>
                              </div>
                            ) : exam.hasAttempt && status === "completed" ? (
                              <span className="text-xs text-amber-600 font-semibold">
                                ⏳ Grading in Progress
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            {status === "available" && (
                              <button
                                className="inline-flex items-center gap-2 px-4 py-2 bg-[#009fe3] text-white rounded-lg text-sm font-semibold hover:bg-cyan-600 transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => handleStartExam(exam)}
                                disabled={startingExamId !== null}
                              >
                                {startingExamId === exam.assignmentId ? (
                                  <>
                                    <FaSpinner className="animate-spin" />
                                    Starting...
                                  </>
                                ) : (
                                  <>
                                    <FaPlay />
                                    Start Exam
                                  </>
                                )}
                              </button>
                            )}
                            {status === "inprogress" && (
                              <button
                                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-semibold hover:bg-amber-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={() => handleContinueExam(exam)}
                                disabled={startingExamId !== null}
                              >
                                {startingExamId === exam.assignmentId ? (
                                  <>
                                    <FaSpinner className="animate-spin" />
                                    Loading...
                                  </>
                                ) : (
                                  <>
                                    <FaClock />
                                    Continue
                                  </>
                                )}
                              </button>
                            )}
                            {status === "completed" &&
                              exam.attemptId &&
                              exam.resultsPublished && (
                                <button
                                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-300 transition-all"
                                  onClick={() => handleViewResult(exam)}
                                >
                                  <FaCheckCircle />
                                  View Result
                                </button>
                              )}
                            {status === "completed" &&
                              !exam.resultsPublished && (
                                <span className="text-xs text-amber-600 font-semibold flex items-center gap-1">
                                  <FaClock />
                                  Grading in Progress
                                </span>
                              )}
                            {/* ✅ FIXED: Handle "upcoming", "expired", and "cancelled" */}
                            {(status === "upcoming" ||
                              status === "expired" ||
                              status === "cancelled") && (
                              <span className="text-gray-400 text-sm">
                                {status === "upcoming" && "Not started"}
                                {status === "expired" && "Expired"}
                                {status === "cancelled" && "Cancelled"}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default StudentMyExams;
